import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import { COMPONENTS } from '@/components/writer/catalog.js';
import BackdropView from '@/components/writer/views/BackdropView.vue';
import CalloutView from '@/components/writer/views/CalloutView.vue';
import ComponentView from '@/components/writer/views/ComponentView.vue';

function attributesOf(defaults) {
  return Object.fromEntries(Object.keys(defaults).map((name) => [name, {
    default: defaults[name],
    rendered: false,
    // Copy and paste inside the writer carries the whole component in this one attribute.
    parseHTML: (element) => {
      try {
        return JSON.parse(element.getAttribute('data-attrs') ?? '{}')[name] ?? defaults[name];
      } catch {
        return defaults[name];
      }
    },
  }]));
}

function componentNode(type, defaults) {
  return Node.create({
    name: type,
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    addAttributes: () => attributesOf(defaults),
    parseHTML: () => [{ tag: `div[data-component="${type}"]` }],
    renderHTML: ({ node }) => ['div', { 'data-component': type, 'data-attrs': JSON.stringify(node.attrs) }],
    addNodeView: () => VueNodeViewRenderer(ComponentView),
  });
}

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: '(paragraph | bulletList | orderedList)+',
  defining: true,
  addAttributes: () => attributesOf({ kind: 'note' }),
  parseHTML: () => [{ tag: 'div[data-component="callout"]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-component': 'callout', 'data-attrs': JSON.stringify({ kind: node.attrs.kind }) }), 0],
  addNodeView: () => VueNodeViewRenderer(CalloutView),
});

export const Backdrop = Node.create({
  name: 'backdrop',
  group: 'block',
  content: '(paragraph | heading | bulletList | orderedList | blockquote)+',
  defining: true,
  addAttributes: () => attributesOf({ image: '', tone: 'dark' }),
  parseHTML: () => [{ tag: 'div[data-component="backdrop"]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-component': 'backdrop', 'data-attrs': JSON.stringify({ image: node.attrs.image, tone: node.attrs.tone }) }), 0],
  addNodeView: () => VueNodeViewRenderer(BackdropView),
});

/** A pasted image only stays when it lives on the web: pasted data would not reach a phone. */
const ImageNode = componentNode('image', { src: '', caption: '', alt: '' }).extend({
  parseHTML: () => [
    { tag: 'div[data-component="image"]' },
    {
      tag: 'img[src^="https://"]',
      getAttrs: (element) => ({ src: element.getAttribute('src'), alt: element.getAttribute('alt') ?? '', caption: '' }),
    },
  ],
});

const Divider = componentNode('divider', { style: 'ornament' }).extend({
  addInputRules() {
    return [{
      find: /^(---|\*\*\*)$/,
      handler: ({ range, chain }) => {
        chain().deleteRange(range).insertContent({ type: 'divider', attrs: { style: 'line' } }).run();
      },
    }];
  },
});

const SIMPLE = COMPONENTS.filter((c) => c.type && !c.content && !['image', 'divider'].includes(c.type));

export const ComponentBlocks = [...SIMPLE.map((c) => componentNode(c.type, c.attrs)), ImageNode, Divider, Callout, Backdrop];
